// Public campaign labels only. Never retain URLs, search terms or click IDs.
export function createJourneyAttribution(win, doc, now = Date.now) {
  const name = 'jeddle_rum_attribution_staging';
  const suffix = '; Domain=staging.jeddle.com; Path=/; Secure; SameSite=Lax';
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const channels = ['direct', 'internal', 'search', 'social', 'referral', 'campaign'];
  const fields = ['source', 'medium', 'campaign', 'content'];
  const readCookie = (key) => doc.cookie.split(/;\s*/).find((part) => part.startsWith(`${key}=`))?.slice(key.length + 1);
  const allowedHost = () => win.location.protocol === 'https:' && ['staging.jeddle.com', 'go.staging.jeddle.com'].includes(win.location.hostname);
  const allowed = () => allowedHost() && [undefined, '', 'allow.v1'].includes(readCookie('jeddle_rum_consent_staging'));
  const slug = (value) => typeof value === 'string' && /^[a-z][a-z0-9_-]{0,63}$/.test(value) && !/(?:password|sso_token|access_token|jwt|@)/.test(value) && !/^[a-f0-9-]{32,}$/.test(value) ? value : undefined;
  const sanitize = (input) => {
    const result = {};
    if (!input || typeof input !== 'object') return result;
    for (const field of fields) {
      const value = slug(input[`attribution_${field}`]);
      if (value) result[`attribution_${field}`] = value;
    }
    if (channels.includes(input.attribution_channel)) result.attribution_channel = input.attribution_channel;
    return result;
  };
  const capture = () => {
    const result = {};
    const params = new URLSearchParams(win.location.search);
    for (const field of fields) {
      const values = params.getAll(`utm_${field}`);
      const value = values.length === 1 ? slug(values[0]?.toLowerCase()) : undefined;
      if (value) result[`attribution_${field}`] = value;
    }
    if (Object.keys(result).length) return { ...result, attribution_channel: 'campaign' };
    let host = '';
    try { host = new URL(doc.referrer).hostname.toLowerCase(); } catch { /* No referrer. */ }
    if (!host) return { attribution_channel: 'direct' };
    if (['staging.jeddle.com', 'go.staging.jeddle.com'].includes(host)) return { attribution_channel: 'internal' };
    const search = /^(?:www\.)?(google\.(?:com|com\.au|co\.uk|co\.nz)|bing\.com|duckduckgo\.com|search\.yahoo\.com)$/;
    const social = /^(?:www\.|m\.|l\.)?(facebook\.com|instagram\.com|linkedin\.com|tiktok\.com|x\.com|twitter\.com|youtube\.com|t\.co)$/;
    return { attribution_channel: search.test(host) ? 'search' : social.test(host) ? 'social' : 'referral' };
  };
  return {
    get(journeyId) {
      try {
        if (!allowed() || !uuid.test(journeyId || '')) return {};
        const raw = readCookie(name);
        let stored;
        if (raw && raw.length < 1600) {
          try { stored = JSON.parse(decodeURIComponent(raw)); } catch { /* Replace invalid cookie. */ }
        }
        const timestamp = now();
        // Bind attribution to the same shared journey and expire after 30m idle.
        const reusable = stored?.v === 1 && stored.journey === journeyId && Number.isFinite(stored.at) && stored.at <= timestamp && timestamp - stored.at < 1800000;
        const data = reusable ? sanitize(stored.data) : capture();
        if (!data.attribution_channel) data.attribution_channel = 'direct';
        // Cookies are synchronous browser I/O. Refresh at most once a minute
        // while still checking shared consent/journey state on every call.
        if (!reusable || timestamp - stored.at >= 60000 || JSON.stringify(stored.data) !== JSON.stringify(data)) {
          const value = encodeURIComponent(JSON.stringify({ v: 1, journey: journeyId, at: timestamp, data }));
          doc.cookie = `${name}=${value}; Max-Age=1800${suffix}`;
        }
        return data;
      } catch { return {}; }
    },
    clear() {
      try { if (allowedHost()) doc.cookie = `${name}=; Max-Age=0${suffix}`; } catch { /* Storage may be disabled. */ }
    },
  };
}

let attribution;
const instance = () => attribution || (attribution = createJourneyAttribution(window, document));
export const getJourneyAttribution = (journeyId) => instance().get(journeyId);
export const clearJourneyAttribution = () => instance().clear();
